import { useState } from "react";
import { CheckCircle2, AlertTriangle, Eye, Camera, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Field, NSelect, Loading, useFetch } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { PhotoPicker, Gallery, useUploadsPending } from "@/components/Photos";
import { api, errMsg, fNum, fDate, todayIso } from "@/lib/api";

const ITEMS = [
  ["voyants", "Voyants au tableau de bord"],
  ["freins", "État des freins"],
  ["pneus", "État des pneumatiques"],
  ["dommages", "Dommages ou nouveaux impacts"],
];
const OPTS = [
  ["ok", "OK", CheckCircle2, "bg-emerald-600 border-emerald-600 text-white"],
  ["surveiller", "À surveiller", Eye, "bg-amber-500 border-amber-500 text-white"],
  ["anomalie", "Anomalie", AlertTriangle, "bg-rose-600 border-rose-600 text-white"],
];
const INIT = { voyants: "ok", freins: "ok", pneus: "ok", dommages: "ok" };

function Segmented({ name, value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2" role="radiogroup">
      {OPTS.map(([v, label, Icon, active]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          data-testid={`check-${name}-${v}`}
          className={`h-14 rounded-lg border-2 flex flex-col items-center justify-center gap-0.5 text-xs font-bold transition-[background-color,border-color,transform] active:scale-95 ${
            value === v ? active : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
          }`}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}
    </div>
  );
}

function KnownDamages({ d }) {
  const open = d.reported.filter((a) => a.status !== "resolue");
  const items = [...d.known.map((x) => ({ id: x.id, title: `${x.zone} — ${x.description}`, sub: `Déjà présent (état des lieux du ${fDate(x.date)})`, photos: x.photos })),
    ...open.map((a) => ({ id: a.id, title: a.description || "Dommage signalé", sub: `Signalé le ${fDate(a.date)} — non résolu`, photos: a.photos || [] }))];
  return (
    <details className="bg-amber-50/60 border border-amber-200 rounded-xl p-4" data-testid="check-known-damages">
      <summary className="cursor-pointer font-semibold text-[#0E2D43] text-sm">Dommages déjà connus sur ce véhicule ({items.length}){!d.current_inspection && " · aucun état des lieux de référence"}</summary>
      <div className="mt-3 space-y-3">
        {!items.length && <div className="text-sm text-slate-500">Aucun dommage connu.</div>}
        {items.map((x) => (
          <div key={x.id} className="text-sm"><b>{x.title}</b><div className="text-xs text-slate-500 mb-1">{x.sub}</div><Gallery ids={x.photos} testid={`known-${x.id}`} /></div>
        ))}
        <div className="text-xs text-slate-500">Pas de nouveau dommage : aucune photo n'est demandée.</div>
      </div>
    </details>
  );
}

export default function ControleForm() {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const { data: vehicles, loading, reload } = useFetch("/vehicles", { type: "livraison" });
  const drivers = useFetch("/drivers", { status: "actif" });
  const [vid, setVid] = useState("");
  const [driver, setDriver] = useState("");
  const [km, setKm] = useState("");
  const [res, setRes] = useState(INIT);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const dmg = useFetch(vid ? `/vehicles/${vid}/damages` : null);
  const [busy, setBusy] = useState(false);
  const uploading = useUploadsPending();
  const [done, setDone] = useState(null);
  if (loading) return <Loading />;
  const list = (vehicles || []).filter((v) => v.status !== "sorti");
  const sel = list.find((v) => v.id === vid);
  const hasAnomaly = Object.values(res).includes("anomalie");

  const submit = async (e) => {
    e.preventDefault();
    if (!vid) return toast.error("Sélectionnez un véhicule");
    if (!driver) return toast.error("Sélectionnez le chauffeur attribué au véhicule");
    if (hasAnomaly && !comment.trim()) return toast.error("Décrivez l'anomalie dans le commentaire");
    if (res.dommages === "anomalie" && !photos.length) return toast.error("Nouveau dommage : ajoutez au moins une photo");
    setBusy(true);
    try {
      const { data } = await api.post("/checks", { vehicle_id: vid, assigned_driver_id: driver, mileage: Number(km), ...res, comment, photos: photos.map((p) => p.file_id) });
      setDone({ ...data, plate: sel.plate, driver: (drivers.data || []).find((d) => d.id === driver)?.name });
      toast.success("Contrôle enregistré");
      reload();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const reset = () => {
    setDone(null);
    setVid("");
    setDriver("");
    setKm("");
    setRes(INIT);
    setComment("");
    setPhotos([]);
  };

  if (done)
    return (
      <div className="max-w-lg mx-auto text-center py-12 rise" data-testid="check-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
        <h2 className="font-display text-2xl font-bold text-[#0E2D43] mt-4">Contrôle enregistré</h2>
        <p className="text-slate-500 mt-2">Véhicule {done.plate} — {fDate(todayIso())}</p>
        <p className="text-slate-500 text-sm mt-1" data-testid="check-success-people">Chauffeur attribué : <b>{done.driver}</b> · Contrôlé par : <b>{user.name}</b></p>
        {done.anomalies_created.length > 0 && (
          <div className="mt-6 text-left bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-800" data-testid="check-anomalies-created">
            <b>{done.anomalies_created.length} alerte(s) créée(s) automatiquement :</b> {done.anomalies_created.join(", ")}
          </div>
        )}
        <button onClick={reset} className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-md bg-[#0E2D43] text-white font-semibold" data-testid="check-new-button">
          <RotateCcw className="w-4 h-4" /> Nouveau contrôle
        </button>
      </div>
    );

  return (
    <div className="max-w-2xl" data-testid="check-form-page">
      <PageHeader overline={`Véhicules de livraison · ${fDate(todayIso())}`} title="Contrôle véhicule" />
      <form onSubmit={submit} className="space-y-5" data-testid="check-form">
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 grid sm:grid-cols-2 gap-4 rise">
          <Field label="Véhicule / immatriculation">
            <NSelect value={vid} onChange={setVid} placeholder="Choisir…" testid="check-vehicle-select"
              options={list.map((v) => ({ value: v.id, label: `${v.plate} · ${v.brand} ${v.model}` }))} />
          </Field>
          <Field label="Contrôle réalisé par">
            <div className="h-10 px-3 flex items-center rounded-md bg-slate-50 border text-sm font-semibold" data-testid="check-controller-name">{user.name}</div>
          </Field>
          <Field label="Chauffeur attribué au véhicule *">
            <NSelect value={driver} onChange={setDriver} placeholder="Choisir le chauffeur…" testid="check-driver-select"
              options={(drivers.data || []).map((d) => ({ value: d.id, label: d.name }))} />
            {drivers.data && !drivers.data.length && (
              <span className="text-xs text-rose-600" data-testid="check-no-driver">Aucun chauffeur actif : {admin ? "ajoutez-en dans Livraison → Chauffeurs" : "contactez l'administrateur"}.</span>
            )}
          </Field>
          <Field label="Kilométrage compteur">
            <Input type="number" inputMode="numeric" required min={1} value={km} onChange={(e) => setKm(e.target.value)} className="h-12 text-lg font-mono bg-white" data-testid="check-mileage-input" />
          </Field>
          <div className="text-xs text-slate-500 flex items-end pb-2">{sel ? `Dernier kilométrage connu : ${fNum(sel.mileage)} km` : ""}</div>
        </div>
        {vid && dmg.data && <KnownDamages d={dmg.data} />}
        {ITEMS.map(([k, label], i) => (
          <div key={k} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 rise" style={{ animationDelay: `${(i + 1) * 50}ms` }}>
            <div className="font-semibold text-[#0E2D43] mb-3">{label}</div>
            <Segmented name={k} value={res[k]} onChange={(v) => setRes({ ...res, [k]: v })} />
            {k === "dommages" && res.dommages === "anomalie" && (
              <div className="mt-4 border-t pt-4" data-testid="check-damage-photos-block">
                <div className="text-sm font-semibold text-rose-700 mb-2">Nouveau dommage : photo(s) obligatoire(s)</div>
                <PhotoPicker items={photos} onChange={setPhotos} testid="check-damage-photos" />
              </div>
            )}
          </div>
        ))}
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-3">
          <Field label={hasAnomaly ? "Commentaire (obligatoire : décrire l'anomalie)" : "Commentaire"}>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="bg-white" data-testid="check-comment-input" />
          </Field>
        </div>
        {hasAnomaly && (
          <div className="text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3" data-testid="check-anomaly-warning">
            Une alerte sera créée automatiquement pour chaque point en anomalie.
          </div>
        )}
        <button type="submit" disabled={busy || uploading > 0} data-testid="check-submit-button"
          className="w-full h-14 rounded-lg bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold text-base flex items-center justify-center gap-2 transition-colors disabled:bg-slate-300 disabled:text-slate-600 sticky bottom-3 shadow-lg">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : uploading ? `Envoi des photos en cours (${uploading})…` : "Valider le contrôle"}
        </button>
      </form>
    </div>
  );
}
